import React from 'react';
import { getStatusColour, DEPENDENCY_COLOURS, COLOUR_OPTIONS } from './GanttUtils';

// Status change popup - shown when clicking a subtask status indicator
const StatusChangePopup = ({ statusPopup, taskStatuses, onStatusChange, onClosureRequired, onClose }) => {
    if (!statusPopup) return null;
    return (
        <div
            className="fixed inset-0 z-50"
            onClick={onClose}
        >
            <div
                className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[150px]"
                style={{ left: statusPopup.x, top: statusPopup.y }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
                    Change Status
                </div>
                {statusPopup.blockers?.length > 0 ? (
                    <>
                        <div className="px-3 py-2 text-sm text-red-600 font-medium">
                            Blocked
                        </div>
                        <div className="px-3 py-1 text-xs text-gray-500 border-t border-gray-100">
                            {statusPopup.blockers.map(b => b.type === 'issue' ? `Issue ${b.id}` : (b.parentTitle ? `${b.parentTitle} / ${b.title}` : b.title)).join(', ')}
                        </div>
                    </>
                ) : (
                    taskStatuses
                        .filter(s => !s.is_archived)
                        .sort((a, b) => a.order - b.order)
                        .map(status => (
                            <button
                                key={status.id}
                                className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                                    statusPopup.row.statusId === status.id ? 'bg-gray-50 font-medium' : ''
                                }`}
                                onClick={() => {
                                    onClose();
                                    if (status.isClosed && !statusPopup.row.isClosed && onClosureRequired) {
                                        onClosureRequired(
                                            statusPopup.row.originalId,
                                            statusPopup.row.text,
                                            statusPopup.row.isMeeting,
                                            status.id
                                        );
                                    } else {
                                        onStatusChange(statusPopup.row.originalId, status.id, statusPopup.row.projectId);
                                    }
                                }}
                            >
                                <span
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: getStatusColour(status.name, status.isClosed) }}
                                />
                                {status.name}
                            </button>
                        ))
                )}
            </div>
        </div>
    );
};

// Assignee change popup - shown when clicking the assignee area on a bar
const AssigneeChangePopup = ({ assigneePopup, members, onAssigneeChange, onClose }) => {
    if (!assigneePopup) return null;
    return (
        <div
            className="fixed inset-0 z-50"
            onClick={onClose}
        >
            <div
                className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] max-h-[300px] overflow-y-auto"
                style={{ left: assigneePopup.x, top: assigneePopup.y }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">
                    Assign To
                </div>
                {/* Unassign option */}
                <button
                    className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
                        !assigneePopup.row.assignedTo ? 'bg-orange-100 font-medium' : ''
                    }`}
                    onClick={() => {
                        onClose();
                        onAssigneeChange(assigneePopup.row.originalId, null);
                    }}
                >
                    <span className="text-gray-500">Unassigned</span>
                </button>
                {/* Member list */}
                {members
                    .filter(m => m.name?.toLowerCase() !== 'admin' && m.email?.toLowerCase() !== 'admin')
                    .map(member => {
                        const isCurrentAssignee = String(assigneePopup.row.assignedTo) === String(member.leadId) || String(assigneePopup.row.assignedTo) === String(member.email);
                        return (
                        <button
                            key={member.leadId}
                            className={`w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                                isCurrentAssignee ? 'bg-orange-100 font-medium' : ''
                            }`}
                            onClick={() => {
                                onClose();
                                onAssigneeChange(assigneePopup.row.originalId, member.leadId);
                            }}
                        >
                            {!isCurrentAssignee && <span className="text-gray-400">+</span>}
                            {member.photo && (
                                <img
                                    src={member.photo}
                                    alt={member.name}
                                    className="w-6 h-6 rounded-full object-cover"
                                />
                            )}
                            <span>{member.name || member.email}</span>
                        </button>
                        );
                    })
                }
            </div>
        </div>
    );
};

// Dependency colour picker popup - shown when right-clicking a dependency arrow
const DependencyColourPicker = ({ colorPopup, onDependencyColorChange, setTopLinkKey, onClose }) => {
    if (!colorPopup) return null;
    return (
        <div
            className="fixed inset-0 z-50"
            onClick={onClose}
        >
            <div
                className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-2 px-2"
                style={{ left: colorPopup.x, top: colorPopup.y }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-2 py-1 text-xs font-medium text-gray-500 border-b border-gray-100 mb-2">
                    Dependency Colour
                </div>
                <div className="grid grid-cols-4 gap-1">
                    {COLOUR_OPTIONS.map(({ name, label }) => (
                        <button
                            key={name}
                            className={`w-8 h-8 rounded-md border-2 transition-all hover:scale-110 ${
                                (colorPopup.link.color || 'gray') === name
                                    ? 'border-gray-800 ring-2 ring-gray-300'
                                    : 'border-transparent hover:border-gray-300'
                            }`}
                            style={{ backgroundColor: DEPENDENCY_COLOURS[name] }}
                            title={label}
                            onClick={() => {
                                const linkKey = `${colorPopup.link.source}_${colorPopup.link.target}`;
                                setTopLinkKey(linkKey);
                                onClose();
                                onDependencyColorChange(colorPopup.link, name);
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

// Custom tooltip - shown when hovering over a bar
const CustomTooltip = ({ tooltip, tooltipPos, tooltipRef, tooltipTimeoutRef, setTooltip, setTooltipPos }) => {
    if (!tooltip) return null;
    return (
        <div
            ref={tooltipRef}
            className="fixed z-50 px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-xs"
            style={{
                left: tooltipPos?.x ?? tooltip.x,
                top: tooltipPos?.y ?? tooltip.y,
                transform: tooltipPos?.arrowPosition === 'top' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)',
                visibility: tooltipPos ? 'visible' : 'hidden'
            }}
            onMouseEnter={() => {
                if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
            }}
            onMouseLeave={() => { setTooltip(null); setTooltipPos(null); }}
        >
            {tooltip.content}
            {/* Arrow - position depends on whether tooltip is above or below */}
            {tooltipPos?.arrowPosition === 'top' ? (
                <div
                    className="absolute left-1/2 -translate-x-1/2 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"
                />
            ) : (
                <div
                    className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"
                />
            )}
        </div>
    );
};

export { StatusChangePopup, AssigneeChangePopup, DependencyColourPicker, CustomTooltip };
